import {
    Canvas,
    Circle,
    controlsUtils,
    Ellipse,
    FabricImage,
    FabricObject,
    FabricText,
    Group,
    Polygon,
    Polyline,
    Rect,
    Triangle
} from "fabric";
import {FABRIC_DEFAULTS} from "./fabric-defaults";
import {FloorplanTableObject, isFloorplanTableObject} from "./fabric-table-object";
import {TABLE_DROP_SIZE, TABLE_MIN_SIZE} from "./constants";
import {
    EdgeSeats,
    FloorPlanItem,
    RectCanvas,
    SeatConfig,
    SeatSlot,
    SeatSlotWithPosition,
    ShapeElement,
    ShapeType,
    SlotEdge,
    TableShape,
    TextCanvas,
    TextElement,
    WallCanvas,
    WallElement,
} from "../types";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";

const DEFAULT_CIRCLE_SEAT_LAYOUT = {
    ...FABRIC_DEFAULTS.table.seats.circle,
};

export const normalizeAngleDeg = (angleDeg: number): number => {
    const normalized = angleDeg % 360;
    return normalized < 0 ? normalized + 360 : normalized;
};

export const snapAngleDeg = (angleDeg: number, stepDeg = DEFAULT_CIRCLE_SEAT_LAYOUT.snapStepDeg): number => {
    const normalizedStep = Math.max(1, stepDeg);
    return normalizeAngleDeg(Math.round(angleDeg / normalizedStep) * normalizedStep);
};

export const angleDegToEdge = (angleDeg: number): SlotEdge => {
    const normalized = normalizeAngleDeg(angleDeg);
    if (normalized >= 315 || normalized < 45) return 'top';
    if (normalized < 135) return 'right';
    if (normalized < 225) return 'bottom';
    return 'left';
};

export const getCircleSeatLayout = (seats?: SeatConfig) => ({
    ...DEFAULT_CIRCLE_SEAT_LAYOUT,
    ...(seats?.circle ?? {}),
});

export const buildCircleSeatAngles = (
    count: number,
    circle: SeatConfig['circle'] | undefined,
): number[] => {
    if (count <= 0) return [];

    const layout = {
        ...DEFAULT_CIRCLE_SEAT_LAYOUT,
        ...(circle ?? {}),
    };
    const startAngleDeg = normalizeAngleDeg(layout.startAngleDeg ?? DEFAULT_CIRCLE_SEAT_LAYOUT.startAngleDeg);
    const sweepAngleDeg = Math.max(0, layout.sweepAngleDeg ?? DEFAULT_CIRCLE_SEAT_LAYOUT.sweepAngleDeg);

    if (count === 1) return [startAngleDeg];
    if (sweepAngleDeg >= 360) {
        return Array.from({length: count}, (_, index) =>
            normalizeAngleDeg(startAngleDeg + (360 / count) * index)
        );
    }
    if (layout.includeEndpoints) {
        return Array.from({length: count}, (_, index) =>
            normalizeAngleDeg(startAngleDeg + (sweepAngleDeg / (count - 1)) * index)
        );
    }

    return Array.from({length: count}, (_, index) =>
        normalizeAngleDeg(startAngleDeg + (sweepAngleDeg / count) * (index + 0.5))
    );
};

const calculateCircleSlotPositions = (
    radius: number,
    seatOffset: number,
    slots: SeatSlot[],
    circle: SeatConfig['circle'] | undefined
): SeatSlotWithPosition[] => {
    const generatedAngles = buildCircleSeatAngles(slots.length, circle);

    return slots.map((slot, i) => {
        const angleDeg = normalizeAngleDeg(slot.angleDeg ?? generatedAngles[i] ?? DEFAULT_CIRCLE_SEAT_LAYOUT.startAngleDeg);
        const radians = (angleDeg * Math.PI) / 180;
        const x = (radius + seatOffset) * Math.sin(radians);
        const y = -(radius + seatOffset) * Math.cos(radians);

        return {...slot, angleDeg, edge: angleDegToEdge(angleDeg), x, y};
    });
};

const calculateRectSlotPositions = (
    width: number,
    height: number,
    seatOffset: number,
    edges: EdgeSeats,
    slots: SeatSlot[]
): SeatSlotWithPosition[] => {
    const result: SeatSlotWithPosition[] = [];
    let position = 0;

    const slotsByEdge: Record<SlotEdge, SeatSlot[]> = {
        top: [],
        right: [],
        bottom: [],
        left: []
    };
    slots.forEach(slot => slotsByEdge[slot.edge].push(slot));

    for (let i = 0; i < edges.top; i++) {
        const x = -width / 2 + (i + 1) * (width / (edges.top + 1));
        const y = -height / 2 - seatOffset;
        const slot = slotsByEdge.top[i] || {
            id: `slot_${position}`,
            edge: 'top' as const,
            position,
            priority: position
        };
        result.push({...slot, x, y});
        position++;
    }

    for (let i = 0; i < edges.right; i++) {
        const x = width / 2 + seatOffset;
        const y = -height / 2 + (i + 1) * (height / (edges.right + 1));
        const slot = slotsByEdge.right[i] || {
            id: `slot_${position}`,
            edge: 'right' as const,
            position,
            priority: position
        };
        result.push({...slot, x, y});
        position++;
    }

    for (let i = 0; i < edges.bottom; i++) {
        const x = -width / 2 + (i + 1) * (width / (edges.bottom + 1));
        const y = height / 2 + seatOffset;
        const slot = slotsByEdge.bottom[i] || {
            id: `slot_${position}`,
            edge: 'bottom' as const,
            position,
            priority: position
        };
        result.push({...slot, x, y});
        position++;
    }

    for (let i = 0; i < edges.left; i++) {
        const x = -width / 2 - seatOffset;
        const y = -height / 2 + (i + 1) * (height / (edges.left + 1));
        const slot = slotsByEdge.left[i] || {
            id: `slot_${position}`,
            edge: 'left' as const,
            position,
            priority: position
        };
        result.push({...slot, x, y});
        position++;
    }

    return result;
};

export const calculateSlotPositions = (
    shape: TableShape,
    width: number,
    height: number,
    seatOffset: number,
    edges: EdgeSeats,
    slots: SeatSlot[],
    circle?: SeatConfig['circle']
): SeatSlotWithPosition[] => {
    if (shape === 'circle') {
        return calculateCircleSlotPositions(Math.min(width, height) / 2, seatOffset, slots, circle);
    }
    return calculateRectSlotPositions(width, height, seatOffset, edges, slots);
};

export const generateSlots = (
    shape: TableShape,
    edges: EdgeSeats,
    existingSlots?: SeatSlot[],
    circle?: SeatConfig['circle']
): SeatSlot[] => {
    const totalSeats = edges.top + edges.right + edges.bottom + edges.left;
    const slots: SeatSlot[] = [];
    let position = 0;

    // Group existing slots by edge and sort by priority within each edge
    const existingByEdge: Record<SlotEdge, SeatSlot[]> = {
        top: [],
        right: [],
        bottom: [],
        left: []
    };
    existingSlots?.forEach(slot => existingByEdge[slot.edge].push(slot));
    // Sort each edge's slots by priority to preserve relative order
    Object.values(existingByEdge).forEach(arr => arr.sort((a, b) => (a.priority ?? a.position) - (b.priority ?? b.position)));

    if (shape === 'circle') {
        const existingSorted = [...(existingSlots ?? [])].sort((a, b) => (a.priority ?? a.position) - (b.priority ?? b.position));
        const generatedAngles = buildCircleSeatAngles(totalSeats, circle);
        for (let i = 0; i < totalSeats; i++) {
            const existing = existingSorted[i];
            const angleDeg = generatedAngles[i] ?? DEFAULT_CIRCLE_SEAT_LAYOUT.startAngleDeg;

            slots.push({
                id: existing?.id ?? `slot_${i}`,
                position: i,
                edge: angleDegToEdge(angleDeg),
                priority: existing?.priority ?? i,
                angleDeg,
            });
        }
    } else {
        (['top', 'right', 'bottom', 'left'] as SlotEdge[]).forEach(edge => {
            for (let i = 0; i < edges[edge]; i++) {
                slots.push({
                    id: `slot_${position}`,
                    position,
                    edge,
                    priority: position, // Temporary, will normalize below
                });
                position++;
            }
        });
    }

    if (shape === 'circle') {
        const existingSorted = [...(existingSlots ?? [])].sort((a, b) => (a.priority ?? a.position) - (b.priority ?? b.position));
        const usedPriorities = new Set<number>();
        const assignedPriorities = slots.map((slot, index) => {
            const existing = existingSorted[index];
            const priority = existing?.priority ?? existing?.position;
            if (priority == null || usedPriorities.has(priority)) return -1;
            usedPriorities.add(priority);
            return priority;
        });

        let nextPriority = 0;
        for (let i = 0; i < assignedPriorities.length; i++) {
            if (assignedPriorities[i] !== -1) continue;
            while (usedPriorities.has(nextPriority)) nextPriority++;
            assignedPriorities[i] = nextPriority;
            usedPriorities.add(nextPriority);
            nextPriority++;
        }

        assignedPriorities
            .map((priority, index) => ({priority, index}))
            .sort((a, b) => a.priority - b.priority)
            .forEach((item, normalizedPriority) => {
                slots[item.index].priority = normalizedPriority;
            });

        return slots;
    }

    // Now assign priorities: preserve existing priorities where possible, then fill gaps
    // Build a mapping of (edge, indexWithinEdge) -> existing priority
    const edgeIndices: Record<SlotEdge, number> = {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
    };
    const assignedPriorities: number[] = [];

    // First pass: assign priorities from existing slots that still exist
    slots.forEach(slot => {
        const edgeSlots = existingByEdge[slot.edge];
        const idx = edgeIndices[slot.edge]++;
        if (idx < edgeSlots.length) {
            // This slot position existed before, keep its priority
            assignedPriorities.push(edgeSlots[idx].priority ?? edgeSlots[idx].position);
        } else {
            // New slot, mark as needing assignment
            assignedPriorities.push(-1);
        }
    });

    // Second pass: assign new sequential priorities to new slots
    // Find max existing priority to continue from, or use total count
    const usedPriorities = new Set(assignedPriorities.filter(p => p >= 0));
    let nextPriority = 0;

    for (let i = 0; i < slots.length; i++) {
        if (assignedPriorities[i] === -1) {
            // Find next available priority
            while (usedPriorities.has(nextPriority)) {
                nextPriority++;
            }
            assignedPriorities[i] = nextPriority;
            usedPriorities.add(nextPriority);
            nextPriority++;
        }
    }

    // Normalize priorities to be 0..n-1 while preserving relative order
    const sortedIndices = assignedPriorities
        .map((priority, index) => ({priority, index}))
        .sort((a, b) => a.priority - b.priority);

    sortedIndices.forEach((item, normalizedPriority) => {
        slots[item.index].priority = normalizedPriority;
    });

    return slots;
};

export const getTableCapacity = (seats?: SeatConfig, fallback = 0): number => {
    if (!seats) return fallback;
    return seats.edges.top + seats.edges.right + seats.edges.bottom + seats.edges.left;
};

export const buildSeatEdgesForCapacity = (count: number): EdgeSeats => {
    const perEdge = Math.floor(count / 4);
    const remainder = count % 4;

    return {
        top: perEdge + (remainder > 0 ? 1 : 0),
        right: perEdge + (remainder > 1 ? 1 : 0),
        bottom: perEdge + (remainder > 2 ? 1 : 0),
        left: perEdge + (remainder > 3 ? 1 : 0),
    };
};

export const buildSeatConfigForCapacity = (
    shape: TableShape,
    count: number,
    currentSeats?: SeatConfig
): SeatConfig => {
    const baseSeats: SeatConfig = currentSeats ?? {
        size: FABRIC_DEFAULTS.table.seats.size,
        offset: FABRIC_DEFAULTS.table.seats.offset,
        edges: FABRIC_DEFAULTS.table.seats.edges,
        slots: [],
        ...(shape === 'circle' ? {circle: {...FABRIC_DEFAULTS.table.seats.circle}} : {}),
    };

    const edges = buildSeatEdgesForCapacity(count);
    return {
        ...baseSeats,
        edges,
        slots: generateSlots(shape, edges, baseSeats.slots, baseSeats.circle),
    };
};


export const createDefaultRectangleTableItem = (id: string, x: number, y: number, tableName: string = 'Table'): FloorPlanItem => {
    const edges: EdgeSeats = {...FABRIC_DEFAULTS.table.seats.edges};
    const slots = generateSlots('rectangle', edges);

    return {
        id,
        table: {
            resource: {
                name: tableName,
                type: 'table',
                meta: {
                    bookableExternally: true,
                    autoAssignExternally: true,
                },
            } as Resource,
            canvas: {
                id,
                type: 'Group',
                left: x,
                top: y,
                width: TABLE_DROP_SIZE,
                height: TABLE_DROP_SIZE,
                fill: FABRIC_DEFAULTS.table.fill,
                stroke: FABRIC_DEFAULTS.table.stroke,
                strokeWidth: FABRIC_DEFAULTS.table.strokeWidth,
                rx: FABRIC_DEFAULTS.table.cornerRadius,
                ry: FABRIC_DEFAULTS.table.cornerRadius,
                textColor: FABRIC_DEFAULTS.table.textColor,
            } as any,
            shape: 'rectangle',
            seats: {
                size: FABRIC_DEFAULTS.table.seats.size,
                offset: FABRIC_DEFAULTS.table.seats.offset,
                edges,
                slots,
            },
        },
    };
};

export const createDefaultCircleTableItem = (id: string, x: number, y: number, tableName: string = 'Round Table'): FloorPlanItem => {
    const edges: EdgeSeats = {...FABRIC_DEFAULTS.table.seats.edges};
    const circle = {...FABRIC_DEFAULTS.table.seats.circle};
    const slots = generateSlots('circle', edges, undefined, circle);
    const diameter = TABLE_DROP_SIZE;

    return {
        id,
        table: {
            resource: {
                name: tableName,
                type: 'table',
                meta: {
                    bookableExternally: true,
                    autoAssignExternally: true,
                },
            } as Resource,
            canvas: {
                id,
                type: 'Group',
                left: x,
                top: y,
                width: diameter,
                height: diameter,
                fill: FABRIC_DEFAULTS.table.fill,
                stroke: FABRIC_DEFAULTS.table.stroke,
                strokeWidth: FABRIC_DEFAULTS.table.strokeWidth,
                textColor: FABRIC_DEFAULTS.table.textColor,
            } as any,
            shape: 'circle',
            seats: {
                size: FABRIC_DEFAULTS.table.seats.size,
                offset: FABRIC_DEFAULTS.table.seats.offset,
                edges,
                slots,
                circle,
            },
        },
    };
};


export const createDefaultRectShapeItem = (id: string, x: number, y: number): FloorPlanItem => ({
    id,
    element: {
        kind: 'shape',
        canvas: {
            id,
            type: 'Rect',
            left: x,
            top: y,
            width: FABRIC_DEFAULTS.shape.width,
            height: FABRIC_DEFAULTS.shape.height,
            fill: FABRIC_DEFAULTS.shape.fill,
            stroke: FABRIC_DEFAULTS.shape.stroke,
            strokeWidth: FABRIC_DEFAULTS.shape.strokeWidth,
            rx: FABRIC_DEFAULTS.shape.cornerRadius,
            ry: FABRIC_DEFAULTS.shape.cornerRadius,
        } as RectCanvas,
        shape: 'rect',
    } as ShapeElement,
});

export const createDefaultCircleShapeItem = (id: string, x: number, y: number): FloorPlanItem => ({
    id,
    element: {
        kind: 'shape',
        canvas: {
            id,
            type: 'Circle',
            left: x,
            top: y,
            width: FABRIC_DEFAULTS.shape.width,
            height: FABRIC_DEFAULTS.shape.height,
            fill: FABRIC_DEFAULTS.shape.fill,
            stroke: FABRIC_DEFAULTS.shape.stroke,
            strokeWidth: FABRIC_DEFAULTS.shape.strokeWidth,
        },
        shape: 'circle',
    } as ShapeElement,
});

export const createDefaultEllipseShapeItem = (id: string, x: number, y: number): FloorPlanItem => ({
    id,
    element: {
        kind: 'shape',
        canvas: {
            id,
            type: 'Ellipse',
            left: x,
            top: y,
            width: FABRIC_DEFAULTS.shape.ellipseWidth,
            height: FABRIC_DEFAULTS.shape.ellipseHeight,
            fill: FABRIC_DEFAULTS.shape.fill,
            stroke: FABRIC_DEFAULTS.shape.stroke,
            strokeWidth: FABRIC_DEFAULTS.shape.strokeWidth,
        },
        shape: 'ellipse',
    } as ShapeElement,
});

export const createDefaultTriangleShapeItem = (id: string, x: number, y: number): FloorPlanItem => ({
    id,
    element: {
        kind: 'shape',
        canvas: {
            id,
            type: 'Polygon',
            left: x,
            top: y,
            width: FABRIC_DEFAULTS.shape.width,
            height: FABRIC_DEFAULTS.shape.height,
            fill: FABRIC_DEFAULTS.shape.fill,
            stroke: FABRIC_DEFAULTS.shape.stroke,
            strokeWidth: FABRIC_DEFAULTS.shape.strokeWidth,
        },
        shape: 'triangle',
    } as ShapeElement,
});

export const createDefaultHexagonShapeItem = (id: string, x: number, y: number): FloorPlanItem => ({
    id,
    element: {
        kind: 'shape',
        canvas: {
            id,
            type: 'Polygon',
            left: x,
            top: y,
            width: FABRIC_DEFAULTS.shape.width,
            height: FABRIC_DEFAULTS.shape.height,
            fill: FABRIC_DEFAULTS.shape.fill,
            stroke: FABRIC_DEFAULTS.shape.stroke,
            strokeWidth: FABRIC_DEFAULTS.shape.strokeWidth,
        },
        shape: 'hexagon',
    } as ShapeElement,
});

export const createDefaultOctagonShapeItem = (id: string, x: number, y: number): FloorPlanItem => ({
    id,
    element: {
        kind: 'shape',
        canvas: {
            id,
            type: 'Polygon',
            left: x,
            top: y,
            width: FABRIC_DEFAULTS.shape.width,
            height: FABRIC_DEFAULTS.shape.height,
            fill: FABRIC_DEFAULTS.shape.fill,
            stroke: FABRIC_DEFAULTS.shape.stroke,
            strokeWidth: FABRIC_DEFAULTS.shape.strokeWidth,
        },
        shape: 'octagon',
    } as ShapeElement,
});

export const createDefaultTextItem = (
    id: string,
    x: number,
    y: number,
    defaultText: string = 'Text'
): FloorPlanItem => {
    return {
        id,
        element: {
            kind: 'text',
            canvas: {
                id,
                type: 'Text',
                left: x,
                top: y,
                text: defaultText,
                fontSize: FABRIC_DEFAULTS.text.fontSize,
                fontFamily: FABRIC_DEFAULTS.text.fontFamily,
                fontWeight: FABRIC_DEFAULTS.text.fontWeight,
                fontStyle: FABRIC_DEFAULTS.text.fontStyle,
                fill: FABRIC_DEFAULTS.text.fill,
                textAlign: FABRIC_DEFAULTS.text.align,
            } as TextCanvas,
        } as TextElement,
    };
};

export const createDefaultVerticalWallItem = (id: string, x: number, y: number): FloorPlanItem => {
    const halfLength = FABRIC_DEFAULTS.wall.length / 2;
    return {
        id,
        element: {
            kind: 'wall',
            canvas: {
                id,
                type: 'Polyline',
                left: x,
                top: y,
                stroke: FABRIC_DEFAULTS.wall.stroke,
                strokeWidth: FABRIC_DEFAULTS.wall.strokeWidth,
                points: [{x: 0, y: -halfLength}, {x: 0, y: halfLength}],
            } as WallCanvas,
        } as WallElement,
    };
};

export const createDefaultHorizontalWallItem = (id: string, x: number, y: number): FloorPlanItem => {
    const halfLength = FABRIC_DEFAULTS.wall.length / 2;
    return {
        id,
        element: {
            kind: 'wall',
            canvas: {
                id,
                type: 'Polyline',
                left: x,
                top: y,
                stroke: FABRIC_DEFAULTS.wall.stroke,
                strokeWidth: FABRIC_DEFAULTS.wall.strokeWidth,
                points: [{x: -halfLength, y: 0}, {x: halfLength, y: 0}],
            } as WallCanvas,
        } as WallElement,
    };
};
export const createWallElement = (item: FloorPlanItem, overrideSelectable?: boolean): Polyline => {
    const element = item.element;
    if (!element || element.kind !== 'wall') throw new Error('createWallElement requires item.element with kind=wall');

    const canvas = element.canvas as WallCanvas;
    const points = canvas.points ?? [{x: 0, y: -50}, {x: 0, y: 50}];
    const selectable = overrideSelectable ?? (canvas.selectable !== false);

    const polylineElement = new Polyline(points, {
        left: canvas.left ?? 0,
        top: canvas.top ?? 0,
        angle: canvas.angle ?? 0,
        stroke: (canvas.stroke ?? FABRIC_DEFAULTS.wall.stroke) as string,
        strokeWidth: canvas.strokeWidth ?? FABRIC_DEFAULTS.wall.strokeWidth,
        strokeDashArray: canvas.strokeDashArray as number[] | undefined,
        strokeUniform: true,
        fill: 'transparent',
        opacity: canvas.opacity ?? 1,
        selectable,
        evented: selectable,
        hasControls: selectable,
        hasBorders: selectable,
        lockMovementX: !selectable,
        lockMovementY: !selectable,
        lockScalingFlip: true,
        hoverCursor: selectable ? 'move' : 'default',
        objectCaching: false,
        snapAngle: 15,
        snapThreshold: 5,
        cornerColor: '#4285f4',
        cornerStrokeColor: '#ffffff',
        cornerSize: 12,
        transparentCorners: false,
        borderColor: '#4285f4',
        borderScaleFactor: 2,
        padding: 4,
    });

    if (polylineElement.controls?.mtr) {
        delete polylineElement.controls.mtr;
    }

    polylineElement.floorPlanItem = item;

    return polylineElement;
};


const createPolygonPoints = (sides: number, radius: number): {
    x: number;
    y: number
}[] => {
    const points: { x: number; y: number }[] = [];
    const angleStep = (2 * Math.PI) / sides;
    const startAngle = -Math.PI / 2;

    for (let i = 0; i < sides; i++) {
        const angle = startAngle + i * angleStep;
        points.push({
            x: radius * Math.cos(angle),
            y: radius * Math.sin(angle),
        });
    }
    return points;
};

export const createShapeElement = (item: FloorPlanItem, overrideSelectable?: boolean): FabricObject => {
    const element = item.element;
    if (!element || element.kind !== 'shape') throw new Error('createShapeElement requires item.element with kind=shape');

    const canvas = element.canvas;
    const shapeType: ShapeType = element.shape ?? 'rect';
    const width = canvas.width ?? FABRIC_DEFAULTS.shape.width;
    const height = canvas.height ?? FABRIC_DEFAULTS.shape.height;
    const selectable = overrideSelectable ?? (canvas.selectable !== false);

    const baseOptions = {
        left: canvas.left ?? 0,
        top: canvas.top ?? 0,
        angle: canvas.angle ?? 0,
        fill: (canvas.fill ?? FABRIC_DEFAULTS.shape.fill) as string,
        stroke: (canvas.stroke ?? FABRIC_DEFAULTS.shape.stroke) as string,
        strokeWidth: canvas.strokeWidth ?? FABRIC_DEFAULTS.shape.strokeWidth,
        strokeDashArray: canvas.strokeDashArray as number[] | undefined,
        strokeUniform: true,
        opacity: canvas.opacity ?? 1,
        selectable,
        evented: selectable,
        hasControls: selectable,
        hasBorders: selectable,
        lockMovementX: !selectable,
        lockMovementY: !selectable,
        lockScalingFlip: true,
        hoverCursor: selectable ? 'move' : 'default',
        snapAngle: 45,
        snapThreshold: 5,
        cornerColor: '#4285f4',
        cornerStrokeColor: '#ffffff',
        cornerSize: 12,
        transparentCorners: false,
        borderColor: '#4285f4',
        borderScaleFactor: 2,
        padding: 4,
    };

    let shapeElement: FabricObject;

    switch (shapeType) {
        case 'rect': {
            const rectCanvas = canvas as RectCanvas;
            shapeElement = new Rect({
                ...baseOptions,
                width,
                height,
                rx: rectCanvas.rx ?? FABRIC_DEFAULTS.shape.cornerRadius,
                ry: rectCanvas.ry ?? FABRIC_DEFAULTS.shape.cornerRadius,
            });
            break;
        }
        case 'circle': {
            const radius = Math.min(width, height) / 2;
            shapeElement = new Circle({
                ...baseOptions,
                radius,
            });
            break;
        }
        case 'ellipse':
            shapeElement = new Ellipse({
                ...baseOptions,
                rx: width / 2,
                ry: height / 2,
            });
            break;
        case 'triangle':
            shapeElement = new Triangle({
                ...baseOptions,
                width,
                height,
            });
            break;
        case 'hexagon': {
            const hexRadius = Math.min(width, height) / 2;
            const hexPoints = createPolygonPoints(6, hexRadius);
            shapeElement = new Polygon(hexPoints, {
                ...baseOptions,
                objectCaching: true,
            });
            break;
        }
        case 'octagon': {
            const octRadius = Math.min(width, height) / 2;
            const octPoints = createPolygonPoints(8, octRadius);
            shapeElement = new Polygon(octPoints, {
                ...baseOptions,
                objectCaching: true,
            });
            break;
        }
        default:
            shapeElement = new Rect({
                ...baseOptions,
                width,
                height,
            });
    }

    if (shapeElement.controls?.mtr) {
        delete shapeElement.controls.mtr;
    }

    if (shapeElement instanceof Polygon) {
        const {
            mtr: _mtr,
            ...standardControls
        } = controlsUtils.createObjectDefaultControls();
        shapeElement.controls = standardControls;
        shapeElement.hasBorders = true;
        shapeElement.hasControls = true;
    }

    if (shapeType === 'circle' || shapeType === 'hexagon' || shapeType === 'octagon') {
        shapeElement.setControlsVisibility({
            ml: false,
            mr: false,
            mt: false,
            mb: false,
        });
    }

    shapeElement.floorPlanItem = item;

    return shapeElement;
};
export const createTextElement = (item: FloorPlanItem, overrideSelectable?: boolean): FabricText => {
    const element = item.element;
    if (!element || element.kind !== 'text') throw new Error('createTextElement requires item.element with kind=text');

    const canvas = element.canvas as TextCanvas;
    const selectable = overrideSelectable ?? (canvas.selectable !== false);

    const textElement = new FabricText(canvas.text ?? FABRIC_DEFAULTS.text.text, {
        left: canvas.left ?? 0,
        top: canvas.top ?? 0,
        angle: canvas.angle ?? 0,
        fontSize: canvas.fontSize ?? FABRIC_DEFAULTS.text.fontSize,
        fontFamily: canvas.fontFamily ?? FABRIC_DEFAULTS.text.fontFamily,
        fontWeight: canvas.fontWeight ?? FABRIC_DEFAULTS.text.fontWeight,
        fontStyle: canvas.fontStyle ?? FABRIC_DEFAULTS.text.fontStyle,
        underline: canvas.underline ?? false,
        fill: (canvas.fill ?? FABRIC_DEFAULTS.text.fill) as string,
        textAlign: canvas.textAlign ?? FABRIC_DEFAULTS.text.align,
        selectable,
        evented: selectable,
        hasControls: selectable,
        hasBorders: selectable,
        lockMovementX: !selectable,
        lockMovementY: !selectable,
        lockScalingFlip: true,
        hoverCursor: selectable ? 'move' : 'default',
        snapAngle: 45,
        snapThreshold: 5,
        cornerColor: '#4285f4',
        cornerStrokeColor: '#ffffff',
        cornerSize: 12,
        transparentCorners: false,
        borderColor: '#4285f4',
        borderScaleFactor: 2,
        padding: 4,
    });

    if (textElement.controls?.mtr) {
        delete textElement.controls.mtr;
    }

    textElement.floorPlanItem = item;

    return textElement;
};

export const createTableGroup = (
    item: FloorPlanItem,
    showSeatsAroundTable: boolean = true,
    overrideSelectable?: boolean,
    hideLabel?: boolean
): FloorplanTableObject => new FloorplanTableObject(item, showSeatsAroundTable, hideLabel, overrideSelectable);

export const updateTableGroup = (
    tableObject: FloorplanTableObject,
    newItem: FloorPlanItem,
    showSeatsAroundTable: boolean,
) => {
    tableObject.applyFloorPlanItem(newItem, showSeatsAroundTable, tableObject.tableHideLabel);
};

export const rebuildTableGroup = (
    oldTable: FabricObject,
    canvas: Canvas,
    newItem: FloorPlanItem,
    showSeatsAroundTable: boolean = true
): FloorplanTableObject => {
    const tableId = oldTable.floorPlanItem?.id;
    const matchingTables = tableId
        ? canvas.getObjects().filter((obj): obj is FabricObject => obj.floorPlanItem?.id === tableId)
        : [];
    const liveTable = matchingTables.includes(oldTable)
        ? oldTable
        : (matchingTables[0] ?? oldTable);
    const centerX = liveTable.left ?? 0;
    const centerY = liveTable.top ?? 0;
    const angle = liveTable.angle ?? 0;
    const stackIndex = canvas.getObjects().indexOf(liveTable);
    const wasActive = canvas.getActiveObject() === liveTable;
    const hideLabel = (liveTable as any).tableHideLabel ?? false;

    if (matchingTables.length > 0) {
        matchingTables.forEach((table) => canvas.remove(table));
    } else {
        canvas.remove(liveTable);
    }

    const table = newItem.table;
    if (!table) throw new Error('rebuildTableGroup requires newItem.table');

    const seats: SeatConfig = table.seats ?? {
        size: FABRIC_DEFAULTS.table.seats.size,
        offset: FABRIC_DEFAULTS.table.seats.offset,
        edges: FABRIC_DEFAULTS.table.seats.edges,
        slots: [],
    };
    const slots = seats.slots.length > 0
        ? seats.slots
        : generateSlots(table.shape, seats.edges, liveTable.floorPlanItem?.table?.seats?.slots, seats.circle);

    const itemWithSlots: FloorPlanItem = {
        ...newItem,
        table: {
            ...table,
            canvas: {
                ...table.canvas,
                left: centerX,
                top: centerY,
                angle,
                scaleX: 1,
                scaleY: 1,
            },
            seats: {
                ...seats,
                slots,
            },
        },
    };

    const newTable = createTableGroup(itemWithSlots, showSeatsAroundTable, undefined, hideLabel);

    if (stackIndex >= 0) {
        canvas.insertAt(stackIndex, newTable);
    } else {
        canvas.add(newTable);
    }
    if (wasActive) {
        canvas.setActiveObject(newTable);
    }
    canvas.renderAll();

    return newTable;
};

const rebuildTextElement = (
    oldText: FabricText,
    canvas: Canvas,
    newItem: FloorPlanItem
): FabricText => {
    const centerX = oldText.left ?? 0;
    const centerY = oldText.top ?? 0;
    const angle = oldText.angle ?? 0;
    const stackIndex = canvas.getObjects().indexOf(oldText);
    const wasActive = canvas.getActiveObject() === oldText;

    canvas.remove(oldText);

    const element = newItem.element;
    if (!element) throw new Error('rebuildTextElement requires newItem.element');

    const itemWithPos: FloorPlanItem = {
        ...newItem,
        element: {
            ...element,
            canvas: {
                ...element.canvas,
                left: centerX,
                top: centerY,
                angle,
            },
        },
    };

    const newText = createTextElement(itemWithPos);

    if (stackIndex >= 0) {
        canvas.insertAt(stackIndex, newText);
    } else {
        canvas.add(newText);
    }
    if (wasActive) {
        canvas.setActiveObject(newText);
    }
    canvas.renderAll();

    return newText;
};

const rebuildShapeElement = (
    oldShape: FabricObject,
    canvas: Canvas,
    newItem: FloorPlanItem
): FabricObject => {
    const centerX = oldShape.left ?? 0;
    const centerY = oldShape.top ?? 0;
    const angle = oldShape.angle ?? 0;
    const stackIndex = canvas.getObjects().indexOf(oldShape);
    const wasActive = canvas.getActiveObject() === oldShape;

    canvas.remove(oldShape);

    const element = newItem.element;
    if (!element) throw new Error('rebuildShapeElement requires newItem.element');

    const itemWithPos: FloorPlanItem = {
        ...newItem,
        element: {
            ...element,
            canvas: {
                ...element.canvas,
                left: centerX,
                top: centerY,
                angle,
            },
        },
    };

    const newShape = createShapeElement(itemWithPos);

    if (stackIndex >= 0) {
        canvas.insertAt(stackIndex, newShape);
    } else {
        canvas.add(newShape);
    }
    if (wasActive) {
        canvas.setActiveObject(newShape);
    }
    canvas.renderAll();

    return newShape;
};

export const rebuildWallElement = (
    oldWall: Polyline,
    canvas: Canvas,
    newItem: FloorPlanItem
): Polyline => {
    const centerX = oldWall.left ?? 0;
    const centerY = oldWall.top ?? 0;
    const angle = oldWall.angle ?? 0;
    const stackIndex = canvas.getObjects().indexOf(oldWall);
    const wasActive = canvas.getActiveObject() === oldWall;

    canvas.remove(oldWall);

    const element = newItem.element;
    if (!element) throw new Error('rebuildWallElement requires newItem.element');

    const itemWithPos: FloorPlanItem = {
        ...newItem,
        element: {
            ...element,
            canvas: {
                ...element.canvas,
                left: centerX,
                top: centerY,
                angle,
            },
        },
    };

    const newWall = createWallElement(itemWithPos);

    if (stackIndex >= 0) {
        canvas.insertAt(stackIndex, newWall);
    } else {
        canvas.add(newWall);
    }
    if (wasActive) {
        canvas.setActiveObject(newWall);
    }
    canvas.renderAll();

    return newWall;
};

export type StrokeStyle = 'solid' | 'dashed' | 'dotted' | 'long-dashed';

export const strokeStyleToDashArray = (style: StrokeStyle): number[] | undefined => {
    switch (style) {
        case 'dashed':
            return [10, 5];
        case 'dotted':
            return [2, 2];
        case 'long-dashed':
            return [15, 5];
        default:
            return undefined;
    }
};

/** Colored overlay rect on the table (status border + background, no text) */
export const createTablePrintOverlay = (
    borderColor: string,
    bgColor: string,
    centerX: number,
    centerY: number,
    width: number,
    height: number,
    shape: 'circle' | 'rectangle' = 'rectangle',
    opacity = 1,
): Group => {
    const bg = shape === 'circle'
        ? new Circle({
            radius: Math.min(width, height) / 2,
            fill: bgColor,
            stroke: borderColor,
            strokeWidth: 2,
            opacity,
        })
        : new Rect({
            width,
            height,
            fill: bgColor,
            stroke: borderColor,
            strokeWidth: 2,
            rx: 6,
            ry: 6,
            opacity,
        });

    const group = new Group([bg], {
        selectable: false,
        evented: false,
        hasControls: false,
        hasBorders: false,
    });

    group.set({left: centerX, top: centerY, originX: 'center', originY: 'center'});
    group.setCoords();
    (group as any).isReservationCard = true;

    return group;
};

/**
 * Print-only reservation card that mirrors the HTML TableOverlayLayerItem:
 *   header label  ("T1-4  2(2/1)/4")
 *   ┌────────────────────────┐
 *   │  Guest Name  (bold)    │
 *   │  lang | Room 410       │
 *   │  18:00 – 19:00         │
 *   └────────────────────────┘  ② badge
 */
export const createReservationCard = (
    data: {
        header: string;
        guestName: string;
        roomNames: string;
        timeRange: string;
        count: number;
        isBirthday?: boolean;
        flagImage?: FabricImage | null;
    },
    centerX: number,
    centerY: number,
    maxWidth?: number,
): FabricObject[] => {
    const padX = 8;
    const padY = 4;
    const lineGap = 2;
    const sectionGap = 4;
    const flagSize = 12;
    const midGap = 4;
    const birthdaySize = 12;
    const birthdayGap = 4;
    const noInteract = {
        selectable: false,
        evented: false,
        hasControls: false,
        hasBorders: false
    };

    // ── Measure text objects ────────────────────────────────────
    const headerLabel = new FabricText(data.header, {
        fontSize: 10, fontFamily: 'sans-serif', fontWeight: 'bold', fill: '#333',
    });

    const nameLabel = new FabricText(data.guestName, {
        fontSize: 13, fontFamily: 'sans-serif', fontWeight: 'bold', fill: '#111',
    });

    const textStyle = {
        fontSize: 11,
        fontFamily: 'sans-serif',
        fill: '#555'
    } as const;
    const guestCountLabel = String(data.count ?? 0);
    const normalizedRoomNames = data.roomNames?.trim() || '';
    const countTextObj = new FabricText(guestCountLabel, textStyle);
    const firstSeparatorNeeded = !!data.flagImage || !!normalizedRoomNames;
    const firstSeparatorObj = firstSeparatorNeeded ? new FabricText('|', textStyle) : null;
    const roomSeparatorObj = data.flagImage && normalizedRoomNames
        ? new FabricText('|', textStyle)
        : null;
    const roomTextObj = normalizedRoomNames
        ? new FabricText(normalizedRoomNames, textStyle)
        : null;
    const birthdayObj = data.isBirthday
        ? new FabricText('🎂', {
            fontSize: 11,
            fontFamily: 'sans-serif',
            fill: '#9c27b0'
        })
        : null;

    // Middle row: "count | [flag] | room"
    let midRowW = countTextObj.width ?? 0;
    if (firstSeparatorObj) midRowW += midGap + (firstSeparatorObj.width ?? 0);
    if (data.flagImage) {
        const fw = data.flagImage.width || flagSize;
        const fh = data.flagImage.height || flagSize;
        data.flagImage.set({
            scaleX: flagSize / fw,
            scaleY: flagSize / fh,
            clipPath: new Ellipse({
                rx: fw / 2,
                ry: fh / 2,
                originX: 'center',
                originY: 'center',
            }),
            ...noInteract,
        });
        midRowW += midGap + flagSize;
    }
    if (roomSeparatorObj) midRowW += midGap + (roomSeparatorObj.width ?? 0);
    if (roomTextObj) midRowW += midGap + (roomTextObj.width ?? 0);
    const birthdayReserveW = birthdayObj ? birthdaySize + birthdayGap : 0;

    const midRowH = Math.max(
        flagSize,
        countTextObj.height ?? 0,
        firstSeparatorObj?.height ?? 0,
        roomSeparatorObj?.height ?? 0,
        roomTextObj?.height ?? 0,
        birthdayObj?.height ?? 0,
    );

    const timeLabel = new FabricText(data.timeRange, {
        fontSize: 11, fontFamily: 'sans-serif', fill: '#555',
    });

    // ── Sizing ──────────────────────────────────────────────────
    const cardInnerW = Math.max(nameLabel.width, midRowW, timeLabel.width);
    const unclamped = Math.max(cardInnerW + padX * 2, 100);
    const cardW = maxWidth ? Math.min(unclamped, maxWidth) : unclamped;

    // Truncate guest name with "…" if it overflows the card content area
    const contentMaxW = cardW - padX * 2;
    if (nameLabel.width > contentMaxW) {
        let text = data.guestName;
        while (text.length > 1) {
            text = text.slice(0, -1);
            nameLabel.set({text: text.trimEnd() + '…'});
            nameLabel.initDimensions();
            if (nameLabel.width <= contentMaxW) break;
        }
    }
    const cardContentH = nameLabel.height + lineGap + midRowH + lineGap + timeLabel.height;
    const cardH = cardContentH + padY * 2;
    const totalH = headerLabel.height + sectionGap + cardH;

    // ── Absolute positioning on the canvas ──────────────────────
    const startY = centerY - totalH / 2;
    const objects: FabricObject[] = [];

    // Clip rect factory — keeps text within card bounds
    const makeTextClip = maxWidth
        ? () => new Rect({
            width: cardW - padX * 2,
            height: cardH,
            originX: 'center',
            originY: 'center'
        })
        : () => undefined;

    // Header — centered above the card
    headerLabel.set({
        left: centerX, top: startY,
        originX: 'center', originY: 'top',
        clipPath: makeTextClip(),
        ...noInteract,
    });
    objects.push(headerLabel);

    // Card background
    const cardTopY = startY + headerLabel.height + sectionGap;
    const cardBg = new Rect({
        left: centerX, top: cardTopY,
        width: cardW, height: cardH,
        originX: 'center', originY: 'top',
        fill: '#ffffff',
        stroke: 'rgba(0,0,0,0.3)',
        strokeWidth: 1,
        rx: 8, ry: 8,
        ...noInteract,
    });
    objects.push(cardBg);

    // Guest name
    nameLabel.set({
        left: centerX, top: cardTopY + padY,
        originX: 'center', originY: 'top',
        clipPath: makeTextClip(),
        ...noInteract,
    });
    objects.push(nameLabel);

    // Middle row (count + flag + room)
    const midRowY = cardTopY + padY + nameLabel.height + lineGap;
    let midX = centerX - birthdayReserveW / 2 - midRowW / 2;
    countTextObj.set({
        left: midX, top: midRowY + (midRowH - countTextObj.height) / 2,
        originX: 'left', originY: 'top',
        clipPath: makeTextClip(),
        ...noInteract,
    });
    objects.push(countTextObj);
    midX += (countTextObj.width ?? 0);
    if (firstSeparatorObj) {
        midX += midGap;
        firstSeparatorObj.set({
            left: midX, top: midRowY + (midRowH - firstSeparatorObj.height) / 2,
            originX: 'left', originY: 'top',
            clipPath: makeTextClip(),
            ...noInteract,
        });
        objects.push(firstSeparatorObj);
        midX += (firstSeparatorObj.width ?? 0);
    }
    if (data.flagImage) {
        midX += midGap;
        data.flagImage.set({
            left: midX, top: midRowY + (midRowH - flagSize) / 2,
            originX: 'left', originY: 'top',
        });
        objects.push(data.flagImage);
        midX += flagSize;
    }
    if (roomSeparatorObj) {
        midX += midGap;
        roomSeparatorObj.set({
            left: midX, top: midRowY + (midRowH - roomSeparatorObj.height) / 2,
            originX: 'left', originY: 'top',
            clipPath: makeTextClip(),
            ...noInteract,
        });
        objects.push(roomSeparatorObj);
        midX += (roomSeparatorObj.width ?? 0);
    }
    if (roomTextObj) {
        midX += midGap;
        roomTextObj.set({
            left: midX, top: midRowY + (midRowH - roomTextObj.height) / 2,
            originX: 'left', originY: 'top',
            clipPath: makeTextClip(),
            ...noInteract,
        });
        objects.push(roomTextObj);
    }
    if (birthdayObj) {
        birthdayObj.set({
            left: centerX + cardW / 2 - padX - birthdaySize,
            top: midRowY + (midRowH - birthdayObj.height) / 2,
            originX: 'left',
            originY: 'top',
            ...noInteract,
        });
        objects.push(birthdayObj);
    }

    // Time range
    timeLabel.set({
        left: centerX,
        top: cardTopY + padY + nameLabel.height + lineGap + midRowH + lineGap,
        originX: 'center', originY: 'top',
        clipPath: makeTextClip(),
        ...noInteract,
    });
    objects.push(timeLabel);

    // +N badge
    if (data.count > 1) {
        const badgeR = 8;
        const badgeX = centerX + cardW / 2;
        const badgeY = cardTopY + cardH - badgeR;
        const badge = new Circle({
            radius: badgeR, fill: '#111',
            left: badgeX, top: badgeY,
            originX: 'center', originY: 'center',
            ...noInteract,
        });
        const badgeNum = new FabricText(String(data.count), {
            fontSize: 10,
            fontFamily: 'sans-serif',
            fontWeight: 'bold',
            fill: '#fff',
            left: badgeX,
            top: badgeY,
            originX: 'center',
            originY: 'center',
            ...noInteract,
        });
        objects.push(badge, badgeNum);
    }

    return objects;
};

export const dashArrayToStrokeStyle = (dashArray?: number[]): StrokeStyle => {
    if (!dashArray || dashArray.length === 0) return 'solid';
    if (JSON.stringify(dashArray) === JSON.stringify([10, 5])) return 'dashed';
    if (JSON.stringify(dashArray) === JSON.stringify([2, 2])) return 'dotted';
    if (JSON.stringify(dashArray) === JSON.stringify([15, 5])) return 'long-dashed';
    return 'solid';
};

export const rebuildElement = (
    element: FabricObject,
    canvas: Canvas,
    newItem: FloorPlanItem,
    showSeatsAroundTable: boolean = true
): FabricObject => {
    const item = element.floorPlanItem;

    if (item?.table && isFloorplanTableObject(element)) {
        return rebuildTableGroup(element, canvas, newItem, showSeatsAroundTable);
    } else if (element instanceof FabricText && item?.element?.kind === 'text') {
        return rebuildTextElement(element, canvas, newItem);
    } else if (element instanceof Polyline && item?.element?.kind === 'wall') {
        return rebuildWallElement(element, canvas, newItem);
    } else if (item?.element?.kind === 'shape') {
        return rebuildShapeElement(element, canvas, newItem);
    } else {
        throw new Error('Unknown element type');
    }
};
